import {
  Calendar,
  CaseSensitive,
  CheckSquare,
  ChevronDownSquare,
  CircleArrowUp,
  Globe,
  Hash,
  type LucideIcon,
  Sigma,
  SquareFunction,
  Type,
  User,
} from 'lucide-react';

import { CalculationInsightCountFactors } from 'common/constants/ideaFormulaFields';
import { IdeaFieldTypes } from 'common/constants/ideas';
import { CalculationFunctions } from 'common/prioritization/CalculationFactorTypes';
import capitalizeFirstLetter from 'common/util/capitalizeFirstLetter';

import type { Option } from 'common/ui/common/select/SelectCommon';

// Icon mapping between field type and Lucide icon
export const FieldTypeDataMap: Record<IdeaFieldTypes, { icon: LucideIcon; label: string }> = {
  [IdeaFieldTypes.boolean]: { icon: CheckSquare, label: 'Checkbox' },
  [IdeaFieldTypes.date]: { icon: Calendar, label: 'Date' },
  [IdeaFieldTypes.dropdown]: { icon: ChevronDownSquare, label: 'Dropdown' },
  [IdeaFieldTypes.multiselect]: { icon: ChevronDownSquare, label: 'Multiselect' },
  [IdeaFieldTypes.formula]: { icon: Sigma, label: 'Formula' },
  [IdeaFieldTypes.number]: { icon: Hash, label: 'Number' },
  [IdeaFieldTypes.score]: { icon: SquareFunction, label: 'Score' },
  [IdeaFieldTypes.multilineText]: { icon: CaseSensitive, label: 'Multiline Text' },
  [IdeaFieldTypes.text]: { icon: Type, label: 'Text' },
  [IdeaFieldTypes.url]: { icon: Globe, label: 'URL' },
  [IdeaFieldTypes.user]: { icon: User, label: 'User' },
} as const;

// These are the factors that are common to all companies and are always available
export const CommonFactorOptions: Option[] = [
  {
    label: 'Company Insight Count',
    value: CalculationInsightCountFactors.companyInsightCount,
    prefixIcon: CircleArrowUp,
  },
  {
    label: 'Insight Count - All',
    value: CalculationInsightCountFactors.insightCount,
    prefixIcon: CircleArrowUp,
  },
  {
    label: 'Insight Count - Nice to have',
    value: CalculationInsightCountFactors.niceToHaveInsightCount,
    prefixIcon: CircleArrowUp,
  },
  {
    label: 'Insight Count - Important',
    value: CalculationInsightCountFactors.importantInsightCount,
    prefixIcon: CircleArrowUp,
  },
  {
    label: 'Insight Count - Must have',
    value: CalculationInsightCountFactors.mustHaveInsightCount,
    prefixIcon: CircleArrowUp,
  },
];

const CalculationFunctionOptions: Option[] = Object.values(CalculationFunctions).map(
  (functionName) => ({
    label: capitalizeFirstLetter(functionName),
    value: functionName,
    prefixIcon: Sigma,
  }),
);

export const getCalculationFunctionOptions = (factor: string | null) => {
  const CountOption: Option = {
    label: 'Count',
    value: CalculationFunctions.count,
    prefixIcon: Sigma,
  };

  if (
    factor &&
    Object.values(CalculationInsightCountFactors).includes(factor as CalculationInsightCountFactors)
  ) {
    return [CountOption];
  }

  return CalculationFunctionOptions;
};
